import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Combobox — searchable select. Input de texto + lista desplegable filtrada por keyword.
 *
 * Props:
 *   options      lista con { value, label, hint? }
 *   value        value actual
 *   onChange     callback al elegir
 *   placeholder  texto del input cuando vacío
 *   disabled     disable todo
 *   emptyLabel   texto cuando no hay match con el filtro
 *
 * UX:
 *   - Click en el input abre el dropdown.
 *   - Escribir filtra opciones por label o hint.
 *   - Flechas arriba/abajo navegan, Enter selecciona, Esc cierra.
 *   - Click fuera cierra.
 *   - Botón X limpia la selección.
 */
export type ComboboxOption = {
  value: string;
  label: string;
  hint?: string;
};

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Selecciona…",
  disabled = false,
  emptyLabel = "Sin coincidencias",
  className,
  allowClear = true,
}: {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  emptyLabel?: string;
  className?: string;
  allowClear?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  // Texto mostrado en el input (fallback al label del seleccionado cuando cerrado)
  const displayText = open ? query : (selected?.label ?? "");

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.hint && o.hint.toLowerCase().includes(q))
    );
  }, [options, query]);

  // Cerrar al click-outside
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    if (open) {
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }
  }, [open]);

  // Reset highlight cuando cambia el filtro
  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = filtered[highlight];
      if (item) {
        onChange(item.value);
        setOpen(false);
        setQuery("");
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  }

  function clearSelection(e: React.MouseEvent) {
    e.stopPropagation();
    onChange("");
    setQuery("");
    inputRef.current?.focus();
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={displayText}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "flex h-8 w-full rounded-md border border-input bg-background pl-8 pr-8 py-1 text-sm",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            "disabled:cursor-not-allowed disabled:opacity-50"
          )}
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
          {allowClear && selected && !disabled && (
            <button
              type="button"
              onClick={clearSelection}
              className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="Limpiar"
            >
              <X size={12} />
            </button>
          )}
          <ChevronDown
            size={14}
            className={cn(
              "text-muted-foreground transition-transform",
              open && "rotate-180"
            )}
          />
        </div>
      </div>

      {open && !disabled && (
        <div className="absolute z-[70] mt-1 max-h-[240px] w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-xl ring-1 ring-black/5">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">{emptyLabel}</div>
          ) : (
            filtered.map((opt, i) => {
              const isHighlighted = i === highlight;
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(opt.value);
                    setOpen(false);
                    setQuery("");
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs",
                    isHighlighted && "bg-accent text-accent-foreground",
                    isSelected && "font-medium"
                  )}
                >
                  <div className="flex-1 truncate">
                    <div>{opt.label}</div>
                    {opt.hint && (
                      <div className="text-[10px] text-muted-foreground">{opt.hint}</div>
                    )}
                  </div>
                  {isSelected && <Check size={12} className="shrink-0 text-primary" />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
